<template>
  <div class="address-page">
    <el-card class="address-card" style="max-width: none">
      <template #header>
        <div class="address-card__header">
          <span v-if="!embedded" class="address-card__title">收货地址</span>
          <span v-else class="address-card__title">地址列表</span>
          <el-button type="primary" plain @click="openModal">
            新增地址
          </el-button>
        </div>
      </template>

      <div v-if="!addressList?.length" class="address-empty">
        <div class="address-empty__icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
          >
            <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" />
            <circle cx="12" cy="10" r="2.5" />
          </svg>
        </div>
        <p class="address-empty__text">暂无收货地址</p>
        <p class="address-empty__hint">点击右上角「新增地址」添加</p>
      </div>

      <div v-else class="address-list">
        <div
          v-for="(item, index) in addressList"
          :key="item.id || index"
          class="address-item"
          :class="{
            'address-item--selected': selectAddress.id === item.id,
            'address-item--default': item.isDefault,
          }"
          @click="handleSelect(item)"
        >
          <div class="address-item__head">
            <div class="address-item__contact">
              <span class="address-item__name">{{ item.name }}</span>
              <span class="address-item__phone">{{ item.phone }}</span>
            </div>
            <span v-if="item.isDefault" class="address-item__badge">默认</span>
          </div>
          <p class="address-item__text">
            {{ item.areaName }}{{ item.detailAddress }}
          </p>
          <div class="address-item__actions" @click.stop>
            <el-button
              v-if="!item.isDefault"
              link
              type="primary"
              @click="handleDefault(item.id)"
            >
              设为默认
            </el-button>
            <el-button link type="primary" @click="editAdd(item.id)">
              修改
            </el-button>
            <el-button link type="danger" @click="deleteAdd(item.id)">
              删除
            </el-button>
          </div>
        </div>
      </div>
    </el-card>

    <el-dialog
      v-model="modal"
      :title="editId ? '修改地址' : '新增地址'"
      width="min(520px, 92vw)"
      class="address-dialog"
      destroy-on-close
      @closed="closeModal"
    >
      <el-form
        ref="ruleFormRef"
        :model="form"
        :rules="rules"
        label-width="88px"
        label-position="right"
        class="address-form"
      >
        <el-form-item label="姓名" prop="name">
          <el-input v-model="form.name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="form.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="省/市/区" prop="areaPath">
          <el-cascader
            v-model="form.areaPath"
            style="width: 100%"
            placeholder="请选择省市区"
            :options="regionData"
            @change="onAreaChange"
          />
        </el-form-item>
        <el-form-item label="详细地址" prop="detailAddress">
          <el-input
            v-model="form.detailAddress"
            type="textarea"
            :rows="3"
            placeholder="请输入详细地址"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="address-dialog__footer">
          <el-button @click="modal = false">取消</el-button>
          <el-button type="primary" @click="onSubmit(ruleFormRef)">
            保存地址
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onBeforeMount, reactive, onMounted } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  addAddress,
  getAddressList,
  deleteAddress,
  getAddressDetail,
  editAddress,
  addDefault,
} from "@/api/address";
import { regionData, CodeToText } from "element-china-area-data";
import { useToast } from "vue-toast-notification";

/* eslint-disable-next-line no-undef */
const props = defineProps({
  selectAddress: {
    type: Object,
    default: null,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

/* eslint-disable-next-line no-undef */
const emit = defineEmits(["getSelectAddress"]);

const selectAddress = ref({ id: "" });
const ruleFormRef = ref();
const addressList = ref();
const editId = ref();
const $toast = useToast();

onMounted(() => {
  if (props.selectAddress) {
    selectAddress.value = props.selectAddress;
  }
});

const handleSelect = (item) => {
  selectAddress.value = item;
  emit("getSelectAddress", selectAddress.value);
};

onBeforeMount(() => {
  getAddressListData();
});

function onAreaChange(value) {
  if (!value || value.length < 3) return;
  form.value.areaName = value
    .map((code) => CodeToText[code] || "")
    .filter(Boolean)
    .join(" ");
}

const getAddressListData = () => {
  getAddressList().then((res) => {
    if (res.code == 200) {
      addressList.value = res.data;
    }
  });
};

const handleDefault = (id) => {
  addDefault({ id }).then((res) => {
    if (res.code == 200) {
      getAddressListData();
      $toast.success("设置成功", {
        position: "top",
      });
    }
  });
};

const modal = ref(false);
const form = ref({
  phone: "",
  name: "",
  isDefault: false,
  areaName: "",
  id: "",
  detailAddress: "",
  areaPath: [],
});

const openModal = () => {
  editId.value = "";
  form.value = {
    phone: "",
    name: "",
    isDefault: false,
    areaName: "",
    id: "",
    detailAddress: "",
    areaPath: [],
  };
  modal.value = true;
};

const closeModal = () => {
  modal.value = false;
  editId.value = "";
  form.value = {
    phone: "",
    name: "",
    isDefault: false,
    areaName: "",
    id: "",
    detailAddress: "",
    areaPath: [],
  };
};

const rules = reactive({
  name: [{ required: true, message: "请输入姓名", trigger: "blur" }],
  phone: [{ required: true, message: "请输入手机号", trigger: "blur" }],
  areaPath: [
    {
      required: true,
      validator: (_rule, value, callback) => {
        if (!value || !Array.isArray(value) || value.length < 3) {
          callback(new Error("请选择省/市/区"));
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  detailAddress: [
    { required: true, message: "请输入详细地址", trigger: "blur" },
  ],
});

const editAdd = (id) => {
  editId.value = id;
  modal.value = true;
  getAddressDetail({ id }).then((res) => {
    if (res.code != 200) return;
    const data = res.data;
    form.value = {
      ...data,
      areaPath: [],
    };
  });
};

const onSubmit = async (formEl) => {
  if (!formEl) return;
  const fuc = editId.value ? editAddress : addAddress;
  await formEl.validate((valid) => {
    if (valid) {
      const payload = {
        name: form.value.name,
        phone: form.value.phone,
        isDefault: form.value.isDefault,
        id: form.value.id,
        detailAddress: form.value.detailAddress,
        areaName: form.value.areaName,
      };
      fuc(payload).then((res) => {
        if (res.code == 200) {
          const msg = editId.value ? "修改成功" : "新增成功";
          $toast.success(msg, { position: "top" });
          closeModal();
          getAddressListData();
        }
      });
    }
  });
};

const deleteAdd = (id) => {
  ElMessageBox.confirm("删除后不可恢复，确定要删除该地址吗？", "删除地址", {
    confirmButtonText: "删除",
    cancelButtonText: "取消",
    type: "warning",
  })
    .then(() => {
      deleteAddress({ id }).then((res) => {
        if (res.code == 200) {
          getAddressListData();
          ElMessage({
            type: "success",
            message: "删除成功",
          });
        }
      });
    })
    .catch(() => {});
};
</script>

<style lang="scss" scoped>
.address-page {
  padding: 0;
  font-family: var(--ios-font, inherit);
}

.address-card {
  :deep(.el-card__header) {
    padding: 18px 22px;
  }

  :deep(.el-card__body) {
    padding: 8px 22px 22px;
  }
}

.address-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.address-card__title {
  font-size: 17px;
  font-weight: 600;
  color: var(--ux-text-primary);
  letter-spacing: -0.02em;
}

.address-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
}

.address-empty__icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ux-text-secondary, #9fb4d6);
  background: var(--ux-glass-bg-subtle);
  border: 0.5px solid var(--ux-glass-border);
  margin-bottom: 16px;

  svg {
    width: 28px;
    height: 28px;
  }
}

.address-empty__text {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ux-text-primary);
}

.address-empty__hint {
  margin: 8px 0 0;
  font-size: 14px;
  color: var(--ux-text-secondary, #9fb4d6);
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.address-item {
  padding: 20px 22px;
  border-radius: var(--ios-radius-lg, 18px);
  border: 0.5px solid var(--ux-glass-border);
  background: var(--ux-glass-bg-subtle);
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    background: var(--ios-fill-secondary);
    box-shadow: var(--ux-glass-shadow);
  }
}

.address-item--selected {
  border-color: rgba(0, 212, 255, 0.55);
  background: rgba(0, 212, 255, 0.08);
  box-shadow: 0 0 0 1px rgba(0, 212, 255, 0.2);
}

.address-item--default {
  border-color: rgba(243, 174, 89, 0.45);
}

.address-item__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.address-item__contact {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
}

.address-item__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ux-text-primary);
}

.address-item__phone {
  font-size: 15px;
  color: var(--ux-text-secondary, #9fb4d6);
}

.address-item__badge {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  color: #f3ae59;
  background: rgba(243, 174, 89, 0.15);
  border: 0.5px solid rgba(243, 174, 89, 0.35);
}

.address-item__text {
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.6;
  color: var(--ux-text-primary);
  word-break: break-word;
}

.address-item__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  padding-top: 14px;
  border-top: 0.5px solid var(--ux-glass-border);

  :deep(.el-button.is-link) {
    padding: 4px 8px;
    font-size: 14px;
  }
}

.address-form {
  padding-top: 4px;

  :deep(.el-form-item) {
    margin-bottom: 22px;
  }

  :deep(.el-form-item__label) {
    color: var(--ux-text-secondary, #9fb4d6);
    font-weight: 500;
  }
}

.address-dialog__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
